import axios from 'axios';
import { API_URL } from '../../config';


// Criar uma instância separada para a operação de refresh token
// Isso evita loops infinitos de interceptores

const refreshApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Interface para a promessa na fila
interface QueuePromise {
  resolve: (value: string | null) => void;
  reject: (reason?: any) => void;
}

// Variável para controlar se já estamos em um processo de refresh de token
let isRefreshing = false;
// Array para armazenar requisições que estão aguardando o refresh do token
let failedQueue: QueuePromise[] = [];

// Função para processar a fila de requisições falhas
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(promise => {
    if(error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Função para obter um novo token usando o refreshToken
const refreshAuthToken = async (): Promise<string> => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('Refresh token não encontrado');
    }
    
    const response = await refreshApi.post('/auth/refresh-token', { refreshToken });
    const data = response.data.data || response.data;
    
    if (!data || !data.accessToken) {
      throw new Error('Resposta inválida ao renovar token');
    }
    
    const newToken = data.accessToken;
    const newRefreshToken = data.refreshToken;
    
    localStorage.setItem('auth_token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }

    return newToken;
  } catch (error) {
    console.error('Falha ao renovar o token:', error);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    throw error;
  }
};

// Adiciona logs para debugar todas as requisições e respostas
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('[Request Error]', error);
    return Promise.reject(error);
  }
);

// Intercepta erros de resposta para tratamento centralizado
api.interceptors.response.use(
  (response) => {
    
    // Padronizar estrutura de resposta
    if (response.data) {
      // Se já tem uma estrutura 'data', mantém como está
      if (response.data.data) {
        return response;
      }
      
      // Se tem uma estrutura 'result', converte para 'data'
      if (response.data.result) {
        response.data = {
          ...response.data,
          data: response.data.result
        };
        delete response.data.result;
        return response;
      }
      
      // Se não tem estrutura, coloca o conteúdo inteiro em 'data'
      if (!response.data.data && !response.data.result) {
        const originalData = response.data;
        if (typeof originalData === 'object' && originalData !== null) {
          // Mantém a estrutura original se já for um objeto
          response.data = {
            data: originalData
          };
        } else {
          // Encapsula valor primitivo em um objeto
          response.data = {
            data: { value: originalData }
          };
        }
      }
    }
    
    return response;
  },
  (error) => {
    // Tratamento de erros específicos
    console.error('[Response Error]', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      baseURL: error.config?.baseURL
    });
    
    if (error.response) {
      // O servidor respondeu com um status de erro
      const { status } = error.response;
      
      if (status === 401) {
        console.warn('401 Unauthorized error detected - accessToken expirado ou inválido');
        const originalRequest = error.config as any;
        
        if (!originalRequest._retry) {
          // Verificar se há um refreshToken disponível
          const refreshToken = localStorage.getItem('refresh_token');
          
          if (!refreshToken) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            
            // Usa setTimeout para evitar redirecionamento imediato que impede visualização do erro
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500); // Espera 1.5 segundos para permitir visualização do erro
            
            return Promise.reject(error);
          }
          
          // Marcar que esta requisição está sendo reprocessada após um refresh de token
          originalRequest._retry = true;
          
          // Se já estamos atualizando o token, adicione esta requisição à fila
          if (isRefreshing) {
            return new Promise<string | null>((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                if (token && originalRequest.headers) {
                  originalRequest.headers['Authorization'] = 'Bearer ' + token;
                }
                return api(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }
          
          // Começar o processo de atualização do token
          isRefreshing = true;
          
          return refreshAuthToken()
            .then(newToken => {
              // Atualiza o token na requisição original e em todas as requisições pendentes
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
              }
              processQueue(null, newToken);
              isRefreshing = false;
              
              // Reenviar a requisição original com o novo token
              return api(originalRequest);
            })
            .catch((refreshError: Error) => {
              // Se falhar ao atualizar o token, processa a fila com erro
              processQueue(refreshError, null);
              isRefreshing = false;
              
              // Limpar dados de autenticação e redirecionar para login
              localStorage.removeItem('auth_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              
              console.error('Erro ao renovar token. Redirecionando para login.', refreshError);
              
              // Delay para que o erro seja visível
              setTimeout(() => {
                window.location.href = '/login';
              }, 1500);
              
              return Promise.reject(refreshError);
            });
        }
      }
      
      if (status === 403) {
        // Acesso não autorizado - Apenas logar o erro, mas não redirecionar
        // para permitir que o componente trate o erro
        console.error('Acesso não autorizado:', error.config?.url);
        // Não redirecionar automaticamente para permitir que as operações
        // de arrastar e soltar de colunas funcionem com usuários não-admin
        // window.location.href = '/unauthorized';
      }
      
      if (status === 404) {
        console.error(`Endpoint não encontrado: ${error.config?.method?.toUpperCase()} ${error.config?.baseURL}${error.config?.url}`);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;